import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TabPanel, TabView } from 'primereact/tabview';
import { AutoComplete } from 'primereact/autocomplete';
import { UserDetailsContext } from 'context/userDetailsContext';
import { MAPPING_STATUSES } from 'constants/mappingStatus';
import { convertToTimezoneWithStartOfDayFormat, convertToTimezoneWithEndOfDayFormat } from 'utils/utility_functions/timezone';
import { listGroupedFiles } from 'services/fileManager/fileManagerServices';

import MailListV2 from './MailListV2';
import MailPreviewV2 from './MailPreviewV2';
import MailCreateModal from './MailCreateModal';
import CaseMappingModal from '../reconciliation/Components/CaseMappingModal';
import ReconcileHeader from '../reconciliation/Components/ReconcileHeader';
import useEmailThreadsV2 from './hooks/useEmailThreadsV2';
import useEmailThreadDetailsV2 from './hooks/useEmailThreadDetailsV2';

import '../mailbox.scss';

/**
 * MailBoxV2 — Mailbox module refactored to use the V2 RDS-backed API.
 *
 * Key differences from V1:
 *  - Offset-based pagination (limit + offset) instead of DynamoDB cursor tokens
 *  - V2 field names: from_email, to_email, email_body, mail_date, mapping_status
 *  - React Query for data fetching in hooks
 *
 * @param {object} caseDetails - Case/Lead details object
 */
const MailBoxV2 = ({ caseDetails }) => {
  const caseId = caseDetails?.case_id;
  const caseName = caseDetails?.case_description?.case_name;
  const isLead = caseDetails?.is_lead ?? false;

  const userContext = useContext(UserDetailsContext);

  const [activeIndex, setActiveIndex] = useState(0);
  // 0 = All, 1 = Unread
  const [unreadActiveIndex, setUnreadActiveIndex] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedThreadPkSk, setSelectedThreadPkSk] = useState({ pk: null, sk: null });
  const [showCompose, setShowCompose] = useState(false);
  const [showMapping, setShowMapping] = useState(false);
  const [readEmails, setReadEmails] = useState({});
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [dateFilter, setDateFilter] = useState('');

  const defaultMappingStatuses = MAPPING_STATUSES.filter((s) => s.key !== 5).map((s) => s.value);

  const [filters, setFilters] = useState({
    caseId,
    mail_date_lower: '',
    mail_date_upper: '',
    mappingStatuses: defaultMappingStatuses,
    userFilter: [],
  });

  // Sync date filter string → lower/upper bounds in filters
  useEffect(() => {
    const dateArray = dateFilter ? (Array.isArray(dateFilter) ? dateFilter : [dateFilter]) : [];
    const mail_date_lower = dateArray[0] ? convertToTimezoneWithStartOfDayFormat(dateArray[0], userContext?.userDetails?.timezone) : '';
    const mail_date_upper = dateArray[0]
      ? convertToTimezoneWithEndOfDayFormat(dateArray[1] ?? dateArray[0], userContext?.userDetails?.timezone)
      : '';
    setFilters((prev) => ({ ...prev, mail_date_lower, mail_date_upper }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const isSent = activeIndex === 1;
  const unreadOnly = unreadActiveIndex === 1;

  const { threads, loading, paginationLoading, hasMore, handleLoadMore, refresh } = useEmailThreadsV2({
    searchKeyword,
    isSent,
    isUnread: unreadOnly,
    filters,
    limit: 20,
  });

  // Threads returned by the server; only hide locally-read items in the Unread view
  const visibleThreads = unreadOnly ? threads.filter((t) => !readEmails[t.pk]) : threads;

  const { emailDetails, setEmailDetails, loading: detailsLoading } = useEmailThreadDetailsV2(selectedThreadPkSk, setReadEmails);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchKeyword(e.target.value);
    }
  };

  /** Append a freshly sent reply into the preview without re-fetching */
  const handleEmailData = (data) => {
    setEmailDetails((prev) => [...prev, data]);
  };

  /** After compose, insert new sent email at top of sent list */
  const handleSentEmailList = (data) => {
    if (isSent) refresh();
  };

  /** Load existing case files for attachment picker */
  const loadExistingFiles = (id) => {
    listGroupedFiles({ case_id: id, all_files: true })
      .then((res) => {
        const arr = Array.isArray(res?.data) ? res.data : [];
        setExistingFiles(
          arr.map((item) => ({
            ...item,
            label: item.display_name,
            value: {
              file_path: item.file_path,
              size: item.size,
              display_name: item.display_name,
              file_name: item.file_name,
              file_type: item.file_type,
              created_by: item.created_by_details,
              is_new_file: true,
              sk: item.sk,
              pk: item.pk,
            },
          }))
        );
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!caseId) return;
    loadExistingFiles(caseId);
  }, [caseId]);

  const tabs = [{ header: 'Inbox' }, { header: 'Sent' }];

  return (
    <div className="text-message-wrap text-message-tab-color">
      {/* Filters / action bar */}
      <ReconcileHeader
        isGlobalMail={false}
        caseValue={caseId}
        dateFilter={dateFilter}
        onDateChange={setDateFilter}
        refreshEmails={refresh}
        setShowComposeModal={setShowCompose}
        selectedEmails={selectedEmails}
        setSelectedEmails={setSelectedEmails}
        setShow={setShowMapping}
        setSelectedEmailThreadPkSk={setSelectedThreadPkSk}
        filters={filters}
        setFilters={setFilters}
        mapping_statuses={defaultMappingStatuses}
        threads={visibleThreads}
        readEmails={readEmails}
        setReadEmails={setReadEmails}
      />

      {/* AI disclaimer */}
      <div className="d-flex align-items-center mb-1 mx-2">
        <span className="d-flex p-1 align-items-center" style={{ lineHeight: '18px', fontSize: '12px' }}>
          <i className="pi pi-info-circle me-2" aria-hidden="true" />
          AI-generated responses may contain errors or inaccuracies. You should not rely on AI responses without independent verification.
        </span>
      </div>

      {/* Two-column layout — mirrors TextMessages inner layout */}
      <div className="d-flex flex-lg-row flex-column text-message-inner">
        {/* Left panel — Inbox/Sent outer tabs + All/Unread inner tabs */}
        <div
          className="d-flex flex-column col-lg-4 col-12 px-md-2 text-msg-list"
          style={{
            minHeight: window.innerWidth < 992 ? '60vh' : 'auto',
            maxHeight: window.innerWidth < 992 ? '60vh' : '100%',
          }}
        >
          {/* Inbox / Sent tabs — outer level, same card-bg tab-view pattern */}
          <TabView
            activeIndex={activeIndex}
            onTabChange={(e) => {
              setActiveIndex(e.index);
              setSelectedThreadPkSk({ pk: null, sk: null });
              setSelectedEmails([]);
              setUnreadActiveIndex(0);
            }}
            className="card-bg tab-view h-100 mt-3 overflow-hidden d-flex flex-column text-message-filter"
          >
            {tabs.map((tab) => (
              <TabPanel header={tab.header} key={tab.header}>
                {/* All / Unread — inner level, same tab pattern */}
                <TabView
                  activeIndex={unreadActiveIndex}
                  onTabChange={(e) => {
                    setUnreadActiveIndex(e.index);
                    setSelectedThreadPkSk({ pk: null, sk: null });
                    setSelectedEmails([]);
                  }}
                >
                  <TabPanel header="All" />
                  <TabPanel header="Unread" />
                </TabView>

                {/* Search */}
                <div className="p-2 contact-select">
                  <span className="p-input-icon-left input-shadow autowidth w-100">
                    <i className="icon-search F-size16 cursor-pointer" aria-hidden="true" />
                    <AutoComplete
                      inputClassName="w-100"
                      className="w-100"
                      value={searchInput}
                      onChange={(e) => {
                        setSearchInput(e.target.value);
                        setSelectedThreadPkSk({});
                      }}
                      placeholder="Search Emails here"
                      onKeyPress={handleSearchKeyPress}
                      autoComplete="off"
                    />
                  </span>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center align-items-center mt-3">
                    <i className="pi pi-spin pi-spinner me-2" aria-hidden="true" />
                    {`Loading ${tab.header} Emails...`}
                  </div>
                ) : (
                  <MailListV2
                    threads={visibleThreads}
                    selectedThreadPk={selectedThreadPkSk?.pk}
                    setSelectedThreadPkSk={setSelectedThreadPkSk}
                    handleLoadMore={handleLoadMore}
                    hasMore={hasMore}
                    paginationLoading={paginationLoading}
                    readEmails={readEmails}
                    setReadEmails={setReadEmails}
                    multiple
                    loading={false}
                    selectedEmails={selectedEmails}
                    setSelectedEmails={setSelectedEmails}
                    isUnreadTab={unreadOnly}
                  />
                )}
              </TabPanel>
            ))}
          </TabView>
        </div>

        {/* Right panel — email preview */}
        <div className="d-flex flex-column col-lg-8 col-12 h-100 px-md-2 text-msg-view">
          {showCompose && (
            <MailCreateModal
              show={showCompose}
              setShow={setShowCompose}
              caseId={caseId}
              isLead={isLead}
              caseName={caseName}
              handleSentEmailList={handleSentEmailList}
              existingFiles={existingFiles}
              className="card-bg"
            />
          )}

          {selectedThreadPkSk?.pk ? (
            detailsLoading ? (
              <div className="card-bg p-3 mt-3 h-100 d-flex align-items-center justify-content-center">
                <i className="pi pi-spin pi-spinner me-2" aria-hidden="true" />
                Loading thread...
              </div>
            ) : (
              <MailPreviewV2
                previewMail={emailDetails}
                caseDetails={caseDetails}
                existingFiles={existingFiles}
                handleEmailData={handleEmailData}
                setShowMapping={setShowMapping}
              />
            )
          ) : (
            <div className="card-bg p-3 mt-3 h-100">
              <div className="d-flex flex-column align-items-center justify-content-center h-100 F-size16 start-conversation">
                <i className="bi bi-envelope" aria-hidden="true" />
                <span>Select an Email</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <CaseMappingModal
        show={showMapping}
        setShow={setShowMapping}
        selectedEmails={selectedEmails}
        setSelectedEmails={setSelectedEmails}
        setSelectedEmailThreadPkSk={setSelectedThreadPkSk}
        refreshEmails={refresh}
        handleReconcile={refresh}
      />
    </div>
  );
};

MailBoxV2.propTypes = {
  caseDetails: PropTypes.shape({
    case_id: PropTypes.string.isRequired,
    is_lead: PropTypes.bool,
    case_description: PropTypes.shape({
      case_name: PropTypes.string,
    }),
  }).isRequired,
};

export default MailBoxV2;
